import { supabase } from '@/integrations/supabase/client';
import {
  DerivativeDetail,
  DerivativeListItem,
  DatabaseResponse,
  DerivativeUpdateResponse
} from '@/utils/supabase/types/derivatives';
import {
  adaptDatabaseToDerivativeCreation,
  adaptDatabaseToDerivativeDetail,
  adaptDatabaseToDerivativeListItem
} from '@/utils/dataAdapters';

// Type for cursor pagination parameters
interface PaginationParams {
  limit: number;
  cursor?: string | null;
  expressionType?: string; // Add expression type filter
}

// Type for the standard API response with pagination
interface PaginatedResponse<T> {
  data: T[];
  cursor: string | null;
  hasMore: boolean;
  error?: any;
  totalCount?: number;
}

export const derivativesApi = {
  /**
   * Get a derivative by ID
   * @param id The derivative ID
   */
  getDerivativeById: async (id: string): Promise<{ data: DerivativeDetail | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.log(`No derivative found with ID ${id}`, error);
        return { data: null, error: 'Derivative not found' };
      }
      
      console.log("Raw derivative data:", data);
      
      // Use the proper adapter function that matches the DerivativeDetail type
      const derivative = adaptDatabaseToDerivativeDetail(data);
      
      return { data: derivative, error: null };
    } catch (error) {
      console.error(`Error in getDerivativeById:`, error);
      return { data: null, error };
    }
  },

  /**
   * Get derivatives for a specific user with cursor pagination
   * @param userId The user ID
   * @param pagination Pagination parameters (limit and cursor)
   */
  getUserDerivatives: async (
    userId: string, 
    pagination: PaginationParams = { limit: 24 }
  ): Promise<PaginatedResponse<DerivativeListItem>> => {
    try {
      console.log(`API: Fetching user derivatives for ${userId}, limit: ${pagination.limit}, cursor: ${pagination.cursor || 'none'}`);
      
      // First, count total available records for this query
      let countQuery = supabase
        .from('derivatives_with_details')
        .select('*', { count: 'exact', head: true })
        .eq('creator_id', userId);
      
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        countQuery = countQuery.eq('expression_type', pagination.expressionType);
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error(`Error counting user derivatives:`, countError);
      } else {
        console.log(`API: Found total ${count} derivatives for user ${userId}`);
      }
        
      let query = supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('creator_id', userId)
        .order('submitted_at', { ascending: false })
        .limit(pagination.limit + 1); // Request one extra to check for more
      
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        query = query.eq('expression_type', pagination.expressionType);
      }
        
      if (pagination.cursor) {
        query = query.lt('submitted_at', pagination.cursor);
      }
        
      const { data, error } = await query;
        
      if (error) {
        console.error(`Error fetching user derivatives:`, error);
        return { data: [], cursor: null, hasMore: false, error, totalCount: count || 0 };
      }
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log(`No derivatives found for user ${userId}`);
        return { data: [], cursor: null, hasMore: false, totalCount: count || 0 };
      }
      
      // Check if there are more items
      const hasMore = data.length > pagination.limit;
      
      // Remove the extra item we requested
      const items = hasMore ? data.slice(0, pagination.limit) : data;
      
      const lastItem = items[items.length - 1];
      const nextCursor = hasMore && lastItem ? lastItem.submitted_at : null;
      
      console.log(`API: Found ${items.length} derivatives for user ${userId}, hasMore: ${hasMore}, nextCursor: ${nextCursor}`);
      
      return { 
        data: items.map(item => adaptDatabaseToDerivativeListItem(item)),
        cursor: nextCursor,
        hasMore,
        totalCount: count || 0
      };
    } catch (error) {
      console.error(`Error in getUserDerivatives:`, error);
      return { data: [], cursor: null, hasMore: false, error };
    }
  },

  /**
   * Get the most recent derivatives with cursor pagination and expression type filter
   * @param pagination Pagination parameters (limit, cursor, and expressionType)
   */
  getLatestDerivatives: async (
    pagination: PaginationParams = { limit: 24 }
  ): Promise<PaginatedResponse<DerivativeListItem>> => {
    try {
      console.log(`API: Fetching latest derivatives, limit: ${pagination.limit}, cursor: ${pagination.cursor || 'none'}, expression: ${pagination.expressionType || 'all'}`);
      
      // Build count query with expression type filter
      let countQuery = supabase
        .from('derivatives_with_details')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved');
      
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        countQuery = countQuery.eq('expression_type', pagination.expressionType);
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error(`Error counting latest derivatives:`, countError);
      } else {
        console.log(`API: Found total ${count} approved derivatives with filter ${pagination.expressionType || 'all'}`);
      }
      
      let query = supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('status', 'approved')
        .order('submitted_at', { ascending: false })
        .limit(pagination.limit + 1); // Request one extra to check for more

      // Add expression type filter if specified
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        query = query.eq('expression_type', pagination.expressionType);
      }

      if (pagination.cursor) {
        query = query.lt('submitted_at', pagination.cursor);
        console.log(`API: Using cursor ${pagination.cursor} to fetch next page`);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`Error fetching latest derivatives:`, error);
        return { data: [], cursor: null, hasMore: false, error, totalCount: count || 0 };
      }
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log(`No latest derivatives found with filter ${pagination.expressionType || 'all'}`);
        return { data: [], cursor: null, hasMore: false, totalCount: count || 0 };
      }
      
      // Check if there are more items by examining if we got more than requested
      const hasMore = data.length > pagination.limit;
      console.log(`API: Got ${data.length} items back, limit was ${pagination.limit}, hasMore=${hasMore}`);
      
      // Remove the extra item we requested
      const items = hasMore ? data.slice(0, pagination.limit) : data;
      
      const lastItem = items[items.length - 1];
      const nextCursor = (hasMore && lastItem) ? lastItem.submitted_at : null;
      
      console.log(`API: Returning ${items.length} latest derivatives, hasMore: ${hasMore}, nextCursor: ${nextCursor || 'none'}`);
      
      return { 
        data: items.map(item => adaptDatabaseToDerivativeListItem(item)),
        cursor: nextCursor,
        hasMore,
        totalCount: count || 0
      };
    } catch (error) {
      console.error(`Error in getLatestDerivatives:`, error);
      return { data: [], cursor: null, hasMore: false, error };
    }
  },

  /**
   * Get the most popular derivatives with cursor pagination
   * @param pagination Pagination parameters (limit and cursor)
   */
  getPopularDerivatives: async (
    pagination: PaginationParams = { limit: 24 }
  ): Promise<PaginatedResponse<DerivativeListItem>> => {
    try {
      console.log(`API: Fetching popular derivatives, limit: ${pagination.limit}, cursor: ${pagination.cursor || 'none'}`);
      
      // We'll sort by views since now we have it in our view
      let query = supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('status', 'approved')
        .order('views', { ascending: false })
        .limit(pagination.limit + 1); // Request one extra item to check if there are more

      if (pagination.cursor) {
        // Use views for the cursor since we're ordering by views
        query = query.lt('views', pagination.cursor);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`Error fetching popular derivatives:`, error);
        return { data: [], cursor: null, hasMore: false, error };
      }
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log(`No popular derivatives found`);
        return { data: [], cursor: null, hasMore: false };
      }
      
      // Check if there are more items
      const hasMore = data.length > pagination.limit;
      // Remove the extra item we requested
      const items = hasMore ? data.slice(0, pagination.limit) : data;
      
      const lastItem = items[items.length - 1];
      // Use views for the cursor since we're ordering by views
      const nextCursor = lastItem?.views?.toString() || null;
      
      console.log(`API: Found ${items.length} popular derivatives, hasMore: ${hasMore}, nextCursor: ${nextCursor}`);
      
      return { 
        data: items.map(item => adaptDatabaseToDerivativeListItem(item)),
        cursor: nextCursor,
        hasMore
      };
    } catch (error) {
      console.error(`Error in getPopularDerivatives:`, error);
      return { data: [], cursor: null, hasMore: false, error };
    }
  },

  /**
   * Get derivatives for a specific playlist with cursor pagination
   * @param playlistId The playlist ID
   * @param pagination Pagination parameters (limit and cursor)
   */
  getDerivativesForPlaylist: async (
    playlistId: string, 
    pagination: PaginationParams = { limit: 24 }
  ): Promise<PaginatedResponse<any>> => {
    try {
      if (!playlistId) {
        console.error('No playlist ID provided');
        return { data: [], cursor: null, hasMore: false, error: 'No playlist ID provided' };
      }
      
      // Build count query with expression type filter
      let countQuery = supabase
        .from('derivatives_with_details')
        .select('*', { count: 'exact', head: true })
        .eq('playlist_id', playlistId)
        .eq('status', 'approved');
      
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        countQuery = countQuery.eq('expression_type', pagination.expressionType);
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error(`Error counting playlist derivatives:`, countError);
      } else {
        console.log(`API: Found total ${count} approved derivatives for playlist ${playlistId} with filter ${pagination.expressionType || 'all'}`);
      }
      
      console.log(`API: Fetching derivatives for playlist ${playlistId}, limit: ${pagination.limit}, cursor: ${pagination.cursor || 'none'}`);
      
      let query = supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('playlist_id', playlistId)
        .eq('status', 'approved')
        .order('submitted_at', { ascending: false })
        .limit(pagination.limit + 1); // Request one extra to check for more
      
      // Add expression type filter if specified
      if (pagination.expressionType && pagination.expressionType !== 'all') {
        query = query.eq('expression_type', pagination.expressionType);
      }
      
      if (pagination.cursor) {
        query = query.lt('submitted_at', pagination.cursor);
        console.log(`API: Using cursor ${pagination.cursor} to fetch next playlist page`);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching derivatives by playlist ID:', error);
        return { data: [], cursor: null, hasMore: false, error, totalCount: count || 0 };
      }

      if (!data || data.length === 0) {
        console.log(`No derivatives found for playlist ${playlistId} with filter ${pagination.expressionType || 'all'}`);
        return { data: [], cursor: null, hasMore: false, totalCount: count || 0 };
      }
      
      // Check if there are more items
      const hasMore = data.length > pagination.limit;
      console.log(`API: Got ${data.length} items for playlist, limit was ${pagination.limit}, hasMore=${hasMore}`);
      
      // Remove the extra item we requested
      const items = hasMore ? data.slice(0, pagination.limit) : data;
      
      const lastItem = items[items.length - 1];
      const nextCursor = (hasMore && lastItem) ? lastItem.submitted_at : null;
      
      console.log(`API: Found ${items.length} derivatives for playlist ${playlistId}, hasMore: ${hasMore}, nextCursor: ${nextCursor}`);
      
      return { 
        data: items.map(item => adaptDatabaseToDerivativeCreation(item)),
        cursor: nextCursor,
        hasMore,
        totalCount: count || 0
      };
    } catch (error) {
      console.error(`Error in getDerivativesForPlaylist:`, error);
      return { data: [], cursor: null, hasMore: false, error };
    }
  },
  
  /**
   * Get all approved derivatives with cursor pagination
   * @param pagination Pagination parameters (limit and cursor)
   */
  getAllApprovedDerivatives: async (
    pagination: PaginationParams = { limit: 24 }
  ): Promise<PaginatedResponse<any>> => {
    try {
      let query = supabase
        .from('derivatives_with_details')
        .select('*')
        .eq('status', 'approved')
        .order('submitted_at', { ascending: false })
        .limit(pagination.limit);
        
      if (pagination.cursor) {
        query = query.lt('submitted_at', pagination.cursor);
      }
        
      const { data, error } = await query;
        
      if (error) {
        console.error('Error fetching all approved derivatives:', error);
        return { data: [], cursor: null, hasMore: false, error };
      }
      
      if (!data || data.length === 0) {
        return { data: [], cursor: null, hasMore: false };
      }
      
      const lastItem = data[data.length - 1];
      const nextCursor = lastItem?.submitted_at || null;
      const hasMore = data.length === pagination.limit;
      
      return { 
        data: data.map(adaptDatabaseToDerivativeCreation),
        cursor: nextCursor,
        hasMore
      };
    } catch (error) {
      console.error('Error in getAllApprovedDerivatives:', error);
      return { data: [], cursor: null, hasMore: false, error };
    }
  },
  
  /**
   * Increment view count for a derivative
   * @param derivativeId The derivative ID
   */
  incrementDerivativeView: async (derivativeId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('increment_derivative_views_secure', {
        derivative_id: derivativeId
      });
      
      if (error) {
        console.error('Error incrementing derivative view:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in incrementDerivativeView:', error);
      return false;
    }
  }
};
